import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  FaMoneyBillWave,
  FaCheck,
  FaTimes,
  FaExclamationCircle,
  FaTags,
  FaCalendarAlt,
  FaFileInvoiceDollar,
  FaListUl,
  FaPaperclip,
  FaReceipt,
} from "react-icons/fa";
import axios from "axios";
import { toast } from "react-toastify";
import useAuth from "../../useAuth";
import { server } from "../../server";

export default function ExpenseForm({ expense, onClose, onSuccess }) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      isActive: true,
      category: "Other",
      paymentMethod: "Cash",
      date: new Date().toISOString().split("T")[0],
    },
  });

  const isEdit = !!expense;
  const { token } = useAuth();

  const [attachmentFile, setAttachmentFile] = useState(null);
  const [attachmentUrl, setAttachmentUrl] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (expense) {
      reset({
        ...expense,
        date: expense.date
          ? new Date(expense.date).toISOString().split("T")[0]
          : "",
      });
      setAttachmentUrl(expense.attachmentUrl || "");
    } else {
      setAttachmentUrl("");
      setAttachmentFile(null);
    }
  }, [expense, reset]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "image/gif",
      "image/webp",
      "application/pdf",
    ];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Only image (JPEG, PNG, GIF, WebP) and PDF files are allowed.");
      e.target.value = "";
      return;
    }

    setAttachmentFile(file);
    setAttachmentUrl(URL.createObjectURL(file));
  };

  const handleRemoveAttachment = () => {
    setAttachmentFile(null);
    setAttachmentUrl("");
  };

  const compressImage = async (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          let quality = 0.8;
          let scale = 0.9;
          const targetMin = 150 * 1024;
          const targetMax = 200 * 1024;

          const attemptCompression = () => {
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");

            canvas.width = img.width * scale;
            canvas.height = img.height * scale;

            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

            canvas.toBlob(
              (blob) => {
                if (!blob) {
                  resolve(file);
                  return;
                }

                if (
                  (blob.size >= targetMin && blob.size <= targetMax) ||
                  quality <= 0.1 ||
                  scale <= 0.1
                ) {
                  resolve(new File([blob], file.name, { type: "image/jpeg" }));
                } else if (blob.size > targetMax) {
                  quality -= 0.1;
                  if (quality < 0.2) {
                    scale -= 0.15;
                    quality = 0.7;
                  }
                  attemptCompression();
                } else {
                  if (scale < 1.0) {
                    scale += 0.05;
                    quality += 0.05;
                    attemptCompression();
                  } else {
                    resolve(new File([blob], file.name, { type: "image/jpeg" }));
                  }
                }
              },
              "image/jpeg",
              quality
            );
          };

          attemptCompression();
        };
      };
    });
  };

  const onSubmit = async (data) => {
    try {
      setIsUploading(true);
      let finalAttachmentUrl = attachmentUrl;

      if (attachmentFile) {
        let fileToUpload = attachmentFile;

        if (attachmentFile.type.startsWith("image/")) {
          if (attachmentFile.size > 200 * 1024) {
            toast.info("Compressing image to 150-200 KB...");
            fileToUpload = await compressImage(attachmentFile);
          }
        }

        const formData = new FormData();
        formData.append("file", fileToUpload);
        formData.append("folderName", "expenses");

        const uploadRes = await axios.post(
          `${server}/auth/upload-document`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );

        if (uploadRes.data && uploadRes.data.url) {
          finalAttachmentUrl = uploadRes.data.url;
        } else {
          throw new Error("Failed to get upload URL from server.");
        }
      }

      const url = isEdit
        ? `${server}/expenses/${expense.id}`
        : `${server}/expenses`;

      const method = isEdit ? "put" : "post";

      const payload = {
        title: data.title,
        description: data.description,
        category: data.category,
        amount: parseFloat(data.amount),
        date: new Date(data.date).toISOString(),
        paymentMethod: data.paymentMethod,
        referenceOrReceiptNo: data.referenceOrReceiptNo,
        attachmentUrl: finalAttachmentUrl || null,
      };

      await axios[method](url, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      toast.success(
        isEdit ? "Expense updated successfully!" : "Expense created successfully!"
      );
      if (onSuccess) onSuccess();
      if (onClose) onClose();
    } catch (error) {
      toast.error(
        error?.response?.data?.message || error.message || "Error saving expense."
      );
      console.error(error);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-xl mx-auto overflow-hidden border border-slate-200 dark:border-slate-800 flex flex-col max-h-[90vh]">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-500 to-red-600 px-5 py-3.5 flex justify-between items-center shrink-0">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white/20 rounded-xl text-white backdrop-blur-md">
            <FaReceipt className="text-base" />
          </div>
          <div>
            <h2 className="text-base font-extrabold text-white leading-tight">
              {isEdit ? "Edit Expense Voucher" : "Create Expense Voucher"}
            </h2>
            <p className="text-[11px] text-red-100 font-medium">
              {isEdit ? "Modify transaction record" : "Record new institutional expense"}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 text-white/80 hover:text-white rounded-lg hover:bg-white/10 transition cursor-pointer"
        >
          <FaTimes size={16} />
        </button>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col flex-1 min-h-0">
        <div className="overflow-y-auto flex-1 p-5 space-y-4 text-xs font-sans">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
            {/* Title */}
            <div className="col-span-1 md:col-span-2">
              <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                Expense Title <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  {...register("title", { required: "Title is required" })}
                  className={`w-full px-3 py-2 bg-slate-50 dark:bg-slate-800/80 border ${
                    errors.title
                      ? "border-red-500 bg-red-50/50"
                      : "border-slate-200 dark:border-slate-700 focus:border-red-500"
                  } rounded-lg text-xs font-bold text-slate-900 dark:text-white outline-none focus:ring-1 focus:ring-red-500 transition-all`}
                  placeholder="e.g. Monthly Electricity Bill"
                />
                {errors.title && (
                  <div className="absolute right-3 top-2.5 text-red-500 text-xs">
                    <FaExclamationCircle />
                  </div>
                )}
              </div>
              {errors.title && (
                <p className="text-[10px] text-red-500 mt-1 font-semibold">
                  {errors.title.message}
                </p>
              )}
            </div>

            {/* Amount */}
            <div className="col-span-1">
              <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                Amount (₹) <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-slate-400 font-bold text-xs">
                  ₹
                </span>
                <input
                  type="number"
                  step="0.01"
                  {...register("amount", { required: "Amount is required" })}
                  className={`w-full pl-7 pr-3 py-2 bg-slate-50 dark:bg-slate-800/80 border ${
                    errors.amount
                      ? "border-red-500 bg-red-50/50"
                      : "border-slate-200 dark:border-slate-700 focus:border-red-500"
                  } rounded-lg text-xs font-bold text-slate-900 dark:text-white outline-none focus:ring-1 focus:ring-red-500 transition-all`}
                  placeholder="0.00"
                />
              </div>
              {errors.amount && (
                <p className="text-[10px] text-red-500 mt-1 font-semibold">
                  {errors.amount.message}
                </p>
              )}
            </div>

            {/* Date */}
            <div className="col-span-1">
              <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                Expense Date <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute left-3 top-2.5 text-slate-400 text-xs">
                  <FaCalendarAlt />
                </div>
                <input
                  type="date"
                  max={new Date().toISOString().split("T")[0]}
                  {...register("date", { required: "Date is required" })}
                  className={`w-full pl-8 pr-3 py-2 bg-slate-50 dark:bg-slate-800/80 border ${
                    errors.date
                      ? "border-red-500 bg-red-50/50"
                      : "border-slate-200 dark:border-slate-700 focus:border-red-500"
                  } rounded-lg text-xs font-bold text-slate-900 dark:text-white outline-none focus:ring-1 focus:ring-red-500 transition-all`}
                />
              </div>
              {errors.date && (
                <p className="text-[10px] text-red-500 mt-1 font-semibold">
                  {errors.date.message}
                </p>
              )}
            </div>

            {/* Category */}
            <div className="col-span-1">
              <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                Category <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute left-3 top-2.5 text-slate-400 text-xs">
                  <FaTags />
                </div>
                <select
                  {...register("category", { required: "Category is required" })}
                  className="w-full pl-8 pr-3 py-2 bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 focus:border-red-500 rounded-lg text-xs font-bold text-slate-900 dark:text-white outline-none focus:ring-1 focus:ring-red-500 transition-all cursor-pointer"
                >
                  <option value="Salary">Salary</option>
                  <option value="Maintenance">Maintenance</option>
                  <option value="Bill">Bill</option>
                  <option value="Event">Event</option>
                  <option value="Supplies">Supplies</option>
                  <option value="Transportation">Transportation</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            {/* Payment Method */}
            <div className="col-span-1">
              <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                Payment Method
              </label>
              <div className="relative">
                <div className="absolute left-3 top-2.5 text-slate-400 text-xs">
                  <FaFileInvoiceDollar />
                </div>
                <select
                  {...register("paymentMethod")}
                  className="w-full pl-8 pr-3 py-2 bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 focus:border-red-500 rounded-lg text-xs font-bold text-slate-900 dark:text-white outline-none focus:ring-1 focus:ring-red-500 transition-all cursor-pointer"
                >
                  <option value="Cash">Cash</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Cheque">Cheque</option>
                  <option value="UPI">UPI</option>
                  <option value="Card">Card</option>
                </select>
              </div>
            </div>

            {/* Reference Number */}
            <div className="col-span-1 md:col-span-2">
              <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                Reference / Receipt No.
              </label>
              <input
                {...register("referenceOrReceiptNo")}
                className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 rounded-lg text-xs font-bold text-slate-900 dark:text-white outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500 transition-all font-mono"
                placeholder="e.g. INV-2026-001"
              />
            </div>

            {/* Description */}
            <div className="col-span-1 md:col-span-2">
              <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                Description / Notes
              </label>
              <div className="relative">
                <div className="absolute left-3 top-2.5 text-slate-400 text-xs">
                  <FaListUl />
                </div>
                <textarea
                  {...register("description")}
                  rows="2"
                  className="w-full pl-8 pr-3 py-2 bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 rounded-lg text-xs font-medium text-slate-900 dark:text-white outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500 transition-all resize-none"
                  placeholder="Additional operational details..."
                />
              </div>
            </div>

            {/* Attachment Receipt */}
            <div className="col-span-1 md:col-span-2">
              <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1 flex items-center gap-1.5">
                <FaPaperclip className="text-slate-400 text-xs" />
                Attachment Receipt{" "}
                <span className="text-slate-400 text-[10px] font-normal">
                  (Images & PDFs, auto-compressed)
                </span>
              </label>
              <div className="flex flex-col gap-2 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-dashed border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={handleFileChange}
                    className="block w-full text-xs text-slate-500 dark:text-slate-400 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-red-50 dark:file:bg-red-950 file:text-red-600 dark:file:text-red-400 hover:file:bg-red-100 cursor-pointer"
                  />
                  {attachmentUrl && (
                    <button
                      type="button"
                      onClick={handleRemoveAttachment}
                      className="text-rose-500 hover:text-rose-700 p-1.5 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-950 transition cursor-pointer"
                      title="Remove attachment"
                    >
                      <FaTimes size={14} />
                    </button>
                  )}
                </div>

                {/* Preview */}
                {attachmentUrl && (
                  <div className="mt-1">
                    {attachmentUrl.toLowerCase().includes(".pdf") ||
                    (attachmentFile && attachmentFile.type === "application/pdf") ? (
                      <div className="flex items-center gap-2.5 p-2 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 shadow-xs text-xs">
                        <span className="text-red-500 text-base font-bold">📄</span>
                        <span className="text-xs text-slate-700 dark:text-slate-300 font-semibold truncate max-w-xs">
                          {attachmentFile ? attachmentFile.name : "Receipt Document (PDF)"}
                        </span>
                        <a
                          href={attachmentUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-red-600 dark:text-red-400 hover:underline text-[11px] font-bold ml-auto"
                        >
                          View PDF
                        </a>
                      </div>
                    ) : (
                      <div className="relative w-32 h-32 rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 shadow-xs bg-white dark:bg-slate-900">
                        <img
                          src={attachmentUrl}
                          alt="Attachment Preview"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex justify-end gap-2.5 p-3.5 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200/60 dark:border-slate-800 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-xs font-bold text-slate-600 dark:text-slate-300 bg-slate-200 dark:bg-slate-800 hover:bg-slate-300 dark:hover:bg-slate-700 rounded-lg transition cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting || isUploading}
            className="px-4 py-2 text-xs font-bold text-white bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 rounded-lg shadow-sm shadow-red-500/20 disabled:opacity-60 transition-all flex items-center gap-1.5 cursor-pointer"
          >
            {isSubmitting || isUploading ? (
              <span className="flex items-center gap-1.5">
                <svg
                  className="animate-spin h-3.5 w-3.5 text-white"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                {isUploading ? "Uploading..." : "Processing..."}
              </span>
            ) : (
              <>
                <FaCheck className="text-xs" /> {isEdit ? "Update Voucher" : "Save Expense"}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
